import React from "react";
import { observer } from "mobx-react";

const SOURCE_BADGES = {
  job_posting: { label: "Job Posting", icon: "💼" },
  funding: { label: "Funding", icon: "💰" },
  timing: { label: "Timing", icon: "⏰" },
  career_history: { label: "Career", icon: "📋" },
  linkedin_post: { label: "LinkedIn Post", icon: "📝" },
  company_news: { label: "News", icon: "📰" },
  tech_stack: { label: "Tech Stack", icon: "🔧" },
  competitive: { label: "Competitive", icon: "⚔️" },
  review_site: { label: "Review", icon: "⭐" },
  deep_dive: { label: "Deep Dive", icon: "🔍" }
};

const URGENCY_STYLES = {
  high:   { bg: "#fee2e2", border: "#fca5a5", color: "#991b1b", label: "🔴 High" },
  medium: { bg: "#fef9c3", border: "#fde68a", color: "#854d0e", label: "🟡 Medium" },
  low:    { bg: "#f3f4f6", border: "#d1d5db", color: "#4b5563", label: "⚪ Low" }
};

// Newer API schema fields
const NEW_FIELD_LABELS = [
  ["why_it_matters",          "Why it matters"],
  ["how_it_connects_to_seller", "How it connects"],
  ["preempt_objection",       "Preempt objection"],
  ["outcome_to_aim_for",      "Outcome to aim for"]
];

const SellingSignals = observer(({ signals }) => {
  if (!signals || signals.length === 0) return null;

  const copyToClipboard = (text) => {
    if (text) navigator.clipboard.writeText(text);
  };

  // Sort by urgency when present, otherwise keep original order
  const sorted = [...signals].sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    const aKey = (a.urgency || "").toLowerCase();
    const bKey = (b.urgency || "").toLowerCase();
    return (order[aKey] ?? 99) - (order[bKey] ?? 99);
  });

  return (
    <div className="drSection">
      <div className="drSectionHeader">
        <span className="drSectionIcon">🎯</span>
        <h3>Selling Signals</h3>
        <span className="drSectionCount">{signals.length}</span>
      </div>
      <div className="drSignalsList">
        {sorted.map((s, idx) => {
          const urgencyKey = (s.urgency || "").toLowerCase();
          const urgency = URGENCY_STYLES[urgencyKey];
          const source = s.source ? (SOURCE_BADGES[s.source] || { label: s.source, icon: "📌" }) : null;

          // Headline text — old (signal) or new (trigger_insight)
          const headline = s.signal || s.trigger_insight || "";
          // Trigger source line (new schema)
          const trigger = s.trigger || "";
          // Recommended hook — old (opener_hook) or new (what_to_say)
          const hook = s.opener_hook || s.what_to_say || "";

          return (
            <div
              key={idx}
              className="drSignalCard"
              style={urgency ? { borderLeft: `3px solid ${urgency.border}` } : undefined}
            >
              {(urgency || source) && (
                <div className="drSignalTop">
                  {urgency && (
                    <span className="drUrgencyBadge" style={{ backgroundColor: urgency.bg, color: urgency.color }}>
                      {urgency.label}
                    </span>
                  )}
                  {source && (
                    <span className="drSourceBadge">{source.icon} {source.label}</span>
                  )}
                </div>
              )}

              {headline && <p className="drSignalText">{headline}</p>}

              {trigger && (
                <p className="drSignalTrigger">
                  <span className="drSignalTriggerLabel">Trigger:</span> {trigger}
                </p>
              )}

              {hook && (
                <div className="drSignalHook">
                  <p className="drHookText">"{hook}"</p>
                  <button className="drCopyBtn" onClick={() => copyToClipboard(hook)}>
                    Copy hook
                  </button>
                </div>
              )}

              {NEW_FIELD_LABELS.some(([key]) => s[key]) && (
                <div className="drSignalDetails">
                  {NEW_FIELD_LABELS.map(([key, label]) => {
                    const value = s[key];
                    if (!value) return null;
                    return (
                      <div className="drSignalDetailRow" key={key}>
                        <span className="drSignalDetailLabel">{label}:</span>
                        <span className="drSignalDetailValue">{value}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default SellingSignals;
